import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { PublicClientApplication } from '@azure/msal-browser';
import { Button } from '@material-ui/core';
import { Form } from '@unform/web';
import { decode } from 'jsonwebtoken';
import { isEmpty, isNull, isArray } from 'lodash';
import * as Yup from 'yup';

import { Input } from '~/components/Form';
import { TextField } from '~/components/Form/v2';
import DialogComponent from '~/componentsLocal/DialogComponent';
import { SENHA_MAXIMO } from '~/constants/default';
import { t } from '~/helpers';
import { validation, validationErrors } from '~/helpers/yupValidations';
import api from '~/services/api';
import { signInRequest, signInMicrosoft } from '~/store/modules/auth/actions';
import { toggleDrawer } from '~/store/modules/drawer/actions';

import * as S from './styles';

const schema = Yup.object().shape({
  username: Yup.string().required(),
  password: Yup.string().required(),
});
const schemaRecoverPass = Yup.object().shape({
  username: Yup.string().required(),
});

export default function SignIn({ location }) {
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const [force, setForce] = useState(false);
  const [informacaoSistema, setInformacaoSistema] = useState(null);

  const [loginUsername, setLoginUserName] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(true);
  const formChangePassRef = useRef(null);
  const turnstileRef = useRef(null);

  const authUser = useSelector((state) => state.auth);
  const websiteJSON = JSON.parse(sessionStorage.getItem('web'));
  const subdominio = window.location.hostname.split('.')[0];

  const [openDialog, setOpendialog] = useState({
    loading: false,
    open: false,
    dialog: 'changepass',
  });

  const dispatch = useDispatch();
  const microsoft = new URL(window.location.href.replace(/#/g, '?'));

  const azureConfig = useMemo(() => JSON.parse(sessionStorage.getItem('web')) || {}, []);
  const webconfig = azureConfig.urlloginremoto;
  const isAzurePkce = azureConfig.fluxologinazure === 'PKCE';
  const isAzureLogin = (!isEmpty(webconfig) || isAzurePkce) && !force;
  const msalClient = useMemo(() => {
    if (!isAzurePkce) return null;
    return new PublicClientApplication({
      auth: {
        clientId: azureConfig.azureclientid,
        authority: `https://login.microsoftonline.com/${azureConfig.azuretenantid}`,
        redirectUri: azureConfig.azureredirecturi,
      },
      cache: { cacheLocation: 'sessionStorage' },
    });
  }, [azureConfig, isAzurePkce]);

  const queryParams = useMemo(() => {
    const uri = new URLSearchParams(location.search);
    return {
      username: uri.get('usuario'),
      password: uri.get('senha'),
    };
  }, [location.search]);

  const microsoftParams = useMemo(
    () => ({
      access_token: microsoft.searchParams.get('access_token'),
      token_type: microsoft.searchParams.get('token_type'),
      expires_in: microsoft.searchParams.get('expires_in'),
      scope: microsoft.searchParams.get('scope'),
      id_token: microsoft.searchParams.get('id_token'),
      state: microsoft.searchParams.get('state'),
      session_state: microsoft.searchParams.get('session_state'),
    }),
    [microsoft]
  );

  const microsoftError = useMemo(
    () => ({
      error: microsoft.searchParams.get('error'),
      error_description: microsoft.searchParams.get('error_description'),
    }),
    []
  );

  // Carrega o script do Turnstile e define o callback global
  useEffect(() => {
    if (isAzureLogin) return;

    if (websiteJSON.captcha !== 'S') return;

    const scriptId = 'turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      document.body.appendChild(script);
    }

    // Callback global para capturar o token
    window.onTurnstileLoad = () => {
      console.log('Turnstile carregado.');
    };

    // Função global para receber o token
    window.handleTurnstileCallback = (token) => {
      console.log('Token recebido:', token);
      setCaptchaToken(token);
      setDisableSubmit(false);
    };

    return () => {
      // Não remove o script para evitar problemas com outros componentes
      setCaptchaToken('');
      setDisableSubmit(true);
    };
  }, [websiteJSON.captcha, isAzureLogin]);

  // Reseta o Turnstile em caso de erro ou expiração
  const resetTurnstile = () => {
    if (window.turnstile && turnstileRef.current) {
      window.turnstile.reset(turnstileRef.current);
      setCaptchaToken('');
      setDisableSubmit(true);
    }
  };

  const handleLoginUserChange = (value) => {
    setLoginUserName(value);
  };

  const handlePwdChange = (value) => {
    setPassword(value);
  };

  const handleSubmit = useCallback(
    async ({ username, passwords }) => {
      let captchaValue = '';
      if (websiteJSON.captcha === 'S') {
        captchaValue = captchaToken;
        if (!captchaValue) {
          toast.error('Por favor, complete a verificação do CAPTCHA.');
          return;
        }
      }

      try {
        const { data } = await api.post('logins', {
          subdominio,
          usuario: loginUsername,
          senha: password,
          captcha: captchaValue,
        });

        const decoded = decode(data);
        const obj = JSON.parse(decoded.data);

        api.defaults.headers.token = data;
        api.defaults.headers.Authorization = `Bearer ${data}`;
        api.defaults.headers.common.Authorization = `Bearer ${data}`;

        if (!obj.forcatrocarsenha) {
          dispatch(signInRequest(data));
          return;
        }
        setOpendialog((prev) => ({ ...prev, open: true, dialog: 'newpass' }));
      } catch (err) {
        setCaptchaToken('');
        if (websiteJSON.captcha === 'S') {
          resetTurnstile();
        }
        if (isArray(err)) err.map((e) => toast.error(e));
        else toast.error(err);
      }
    },
    [loginUsername, password, captchaToken]
  );

  const handleRedirectToMicrosoft = useCallback(() => {
    if (isAzurePkce && msalClient != null) {
      msalClient.loginRedirect({ scopes: ['openid', 'profile'] });
    } else if (webconfig != null) {
      window.location.href = webconfig;
    }
  }, [isAzurePkce, msalClient, webconfig]);

  const handleRedirectFromMicrosoft = useCallback(() => {
    dispatch(signInMicrosoft({ ...microsoftParams, subdominio }));
  }, [microsoftParams, subdominio]);

  const handleGetInformacaoSistema = useCallback(async () => {
    try {
      const { data } = await api.get('info');
      if (isNull(data)) return;

      const buildReact = `${process.env.REACT_APP_TAG}`;
      const buildSistema = data.build;

      if (buildSistema === '${buildVersion}') return;

      setInformacaoSistema(data);

      if (buildReact !== buildSistema) {
        window.location.reload();
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  window.addEventListener('resize', () => {
    setInnerWidth(window.innerWidth);
  });

  useEffect(() => {
    setDisableSubmit(websiteJSON.captcha === 'S');
    handleGetInformacaoSistema();
  }, []);

  useEffect(() => {
    dispatch(toggleDrawer(innerWidth > 425));
  }, [innerWidth]);

  useEffect(() => {
    if (!isNull(microsoftError.error)) {
      toast.error(microsoftError.error_description);
    }
  }, [microsoftError.error, microsoftError.error_description]);

  useEffect(() => {
    if (!isAzurePkce && isNull(microsoftError.error) && !isNull(microsoftParams.access_token)) {
      handleRedirectFromMicrosoft();
    }
  }, [isAzurePkce, microsoftError.error, microsoftParams.access_token]);

  useEffect(() => {
    if (!isAzurePkce || msalClient == null) return;
    async function handlePkceRedirect() {
      try {
        await msalClient.initialize();
        const response = await msalClient.handleRedirectPromise();
        if (response != null && response.idToken) {
          dispatch(signInMicrosoft({ id_token: response.idToken, subdominio }));
        }
      } catch (err) {
        toast.error(err.message || err);
      }
    }
    handlePkceRedirect();
  }, [dispatch, isAzurePkce, msalClient, subdominio]);

  useEffect(() => {
    if (!isNull(queryParams.username) && !isNull(queryParams.password)) {
      handleSubmit({ ...queryParams });
    }
  }, [queryParams]);

  const handleSubmitRecoverPass = useCallback(async ({ username }) => {
    try {
      setOpendialog((prev) => ({ ...prev, loading: true, open: true }));
      await api.post('logins/lostpassword', {
        usuarioouemail: username,
        subdominio: window.location.hostname.split('.')[0],
      });

      toast.success(t('Verifique seu email.'));
      setLoginUserName('');
      setPassword('');
      setOpendialog((prev) => ({
        ...prev,
        loading: false,
        open: false,
        dialog: '',
      }));
    } catch (err) {
      setOpendialog((prev) => ({
        ...prev,
        loading: false,
      }));
      if (isArray(err)) err.map((e) => toast.error(e));
      else toast.error(err);
    }
  });

  const handleSubmitChangePass = useCallback(
    async (data) => {
      try {
        const schemaChangePass = Yup.object().shape({
          senhaatual: Yup.string()
            .required('Campo obrigatório.')
            .max(SENHA_MAXIMO, 'Este campo deve ter no máximo 30 caracteres'),
          senhanova: Yup.string()
            .required('Campo obrigatório.')
            .max(SENHA_MAXIMO, 'Este campo deve ter no máximo 30 caracteres'),
          senhanovaconfirmada: Yup.string()
            .required('Campo obrigatório.')
            .max(SENHA_MAXIMO, 'Este campo deve ter no máximo 30 caracteres'),
        });

        await validation(schemaChangePass, data);

        setOpendialog((prev) => ({ ...prev, loading: true, open: true }));

        await api.post('logins/changepassword', {
          subdominio,
          codigousuario: loginUsername,
          senhaatual: data.senhaatual,
          senhanova: data.senhanova,
          senhanovaconfirmacao: data.senhanovaconfirmada,
        });

        setLoginUserName('');
        setPassword('');

        toast.success(t('Senha alterada com sucesso.'));
        setOpendialog((prev) => ({
          ...prev,
          loading: false,
          open: false,
          dialog: '',
        }));
      } catch (err) {
        validationErrors(err, formChangePassRef);
        if (isArray(err)) err.map((e) => toast.error(e));
        else toast.error(err);
      } finally {
        setOpendialog((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    },
    [loginUsername]
  );

  if (!isNull(queryParams.username) && !isNull(queryParams.password))
    return (
      <div style={{ textTransform: 'uppercase', textAlign: 'center' }}>
        Carregando Dados da Aplicação
      </div>
    );

  if (isAzureLogin)
    return (
      <S.Wrapper>
        <Button
          fullWidth
          type="button"
          color="primary"
          variant="contained"
          onClick={handleRedirectToMicrosoft}
        >
          Login
        </Button>
        <S.BackToLogin onClick={() => setForce(true)}>
          Login Alternativo
        </S.BackToLogin>
      </S.Wrapper>
    );

  return (
    <>
      <Form schema={schema} onSubmit={handleSubmit}>
        <Input
          name="username"
          label="Nome de usuario"
          required
          variant="standard"
          size="medium"
          autoComplete="off"
          value={loginUsername}
          onChange={(event) => {
            handleLoginUserChange(event.target.value);
          }}
        />

        <Input
          type="password"
          name="password"
          label="Senha"
          required
          variant="standard"
          size="medium"
          autoComplete="off"
          value={password}
          onChange={(event) => {
            handlePwdChange(event.target.value);
          }}
        />

        {websiteJSON.captcha === 'S' && (
          <div
            ref={turnstileRef}
            className="cf-turnstile"
            data-sitekey="0x4AAAAAAA90KLeAPYJbgleK" // Sua Site Key
            data-callback="handleTurnstileCallback" // Callback global
            data-error-callback={() => {
              toast.error('Erro ao carregar o Turnstile.');
              setDisableSubmit(true);
            }}
            data-expired-callback={() => {
              setCaptchaToken('');
              setDisableSubmit(true);
            }}
          />
        )}

        {websiteJSON.recuperasenhaconfigsistema === 'S' && (
          <a
            className="lostpassword-button"
            onClick={() =>
              setOpendialog((prev) => ({
                ...prev,
                open: !prev.open,
                dialog: 'changepass',
              }))
            }
          >
            Esqueceu a senha?
          </a>
        )}
        <Button
          color="primary"
          variant="contained"
          fullWidth
          type="submit"
          disabled={authUser.loading || disableSubmit}
        >
          {authUser.loading ? 'Carregando...' : 'Login'}
        </Button>
      </Form>
      <DialogComponent
        open={openDialog.open}
        handleClose={() =>
          setOpendialog((prev) => ({ ...prev, open: !prev.open }))
        }
        title={
          openDialog.dialog === 'changepass'
            ? 'Recuperar Senha'
            : 'Criar nova senha'
        }
      >
        {openDialog.dialog === 'changepass' ? (
          <Form schema={schemaRecoverPass} onSubmit={handleSubmitRecoverPass}>
            <Input
              name="username"
              label="Nome de usuario"
              required
              variant="standard"
              size="medium"
              autoComplete="off"
            />
            <Button
              color="primary"
              variant="contained"
              fullWidth
              type="submit"
              disabled={openDialog.loading}
            >
              {openDialog.loading ? 'Carregando...' : 'Recuperar Senha'}
            </Button>
          </Form>
        ) : (
          <Form onSubmit={handleSubmitChangePass} ref={formChangePassRef}>
            <TextField
              name="senhaatual"
              type="password"
              label={t('Senha Atual')}
              autoComplete="off"
            />
            <TextField
              name="senhanova"
              type="password"
              label={t('Nova Senha')}
              autoComplete="off"
            />
            <TextField
              name="senhanovaconfirmada"
              type="password"
              label={t('Redigite a senha')}
              autoComplete="off"
            />
            <Button
              color="primary"
              variant="contained"
              fullWidth
              type="submit"
              disabled={openDialog.loading}
            >
              {openDialog.loading ? 'Carregando...' : 'Criar nova senha'}
            </Button>
          </Form>
        )}
      </DialogComponent>
    </>
  );
}
